import { CommonModule } from '@angular/common';
import {
  Component,
  DoCheck,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { ContractTypeFullNamePipe } from '../contract-type-full-name-pipe';
import { ContractType, ContractTypes } from '../contract.model';

@Component({
  selector: 'app-select-contract-type',
  imports: [CommonModule, MatFormFieldModule, MatSelectModule, ContractTypeFullNamePipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectContractType),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectContractType),
      multi: true,
    },
  ],
  templateUrl: './select-contract-type.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './select-contract-type.scss',
})
export class SelectContractType implements OnInit, DoCheck {
  private injector = inject(Injector);

  @Input() label = 'Type de contrat';
  @Input() value: ContractType | undefined;
  @Input() required = false;
  @Output() selectionChange = new EventEmitter<ContractType>();
  public ngControl?: NgControl;
  disabled = false;

  @ViewChild(MatSelect) private readonly matSelect?: MatSelect;

  /**
   * Reflète l'état d'erreur du FormControl parent sur le mat-select interne,
   * afin que le mat-form-field passe en rouge lorsque la valeur est invalide.
   */
  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => {
      const ctrl = this.control;
      return !!(ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty));
    },
  };

  ContractTypes = ContractTypes;

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      this.required = this.hasRequiredValidator(this.ngControl.control!);
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor pour SelectCompany. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  /**
   * Le mat-select interne n'est pas lié à un NgControl, donc Angular Material ne
   * recalcule jamais son état d'erreur tout seul. On le déclenche ici pour que le
   * mat-form-field reflète l'état invalide du FormControl parent (bordure rouge).
   */
  ngDoCheck(): void {
    this.matSelect?.updateErrorState();
  }

  onChange: (value: ContractType | undefined) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: ContractType | undefined): void {
    this.value = value;
  }

  registerOnChange(fn: (value: ContractType | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.required || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && !this.value) {
      return { required: true };
    }

    return null;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: ContractType): void {
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(value);
  }
}
